<lib-modal
  [closeLabel]="'general.close' | translate"
  [visible]="visible()"
  [title]="'testCases.form.testSteps.libraryDialog.title' | translate"
  [contentTemplate]="resolvedTemplate ?? undefined"
  [saveText]="saveLabel()"
  [cancelText]="'testCases.form.testSteps.libraryDialog.cancel' | translate"
  [disabled]="!canApply()"
  (save)="onApply()"
  (cancel)="onCancel()"
  (visibleChange)="visible.set($event)"
></lib-modal>

<ng-template #libraryContent>
  <div class="tsl">
    <p class="tsl__subtitle">
      {{ subtitleKey() | translate: { count: selectedCount() } }}
    </p>

    <div class="tsl__search">
      <mat-icon class="tsl__search-icon" aria-hidden="true">search</mat-icon>
      <input
        class="tsl__search-input"
        type="text"
        [value]="searchTerm()"
        (input)="onSearch($event)"
        [attr.aria-label]="'testCases.form.testSteps.libraryDialog.search' | translate"
        [placeholder]="'testCases.form.testSteps.libraryDialog.searchPlaceholder' | translate"
      />
    </div>

    <div
      class="tsl__filters"
      role="group"
      [attr.aria-label]="'testCases.form.testSteps.libraryDialog.filterLabel' | translate"
    >
      @for (filter of categoryFilters; track filter) {
        <button
          type="button"
          class="tsl__filter-btn"
          [class.tsl__filter-btn--active]="isActiveFilter(filter)"
          [attr.aria-pressed]="isActiveFilter(filter)"
          (click)="setFilter(filter)"
        >
          {{ filterLabel(filter) }}
        </button>
      }
    </div>

    @if (loading()) {
      <div class="tsl__loading">
        <mat-spinner diameter="28" />
      </div>
    } @else if (error()) {
      <p class="tsl__error" role="alert">{{ 'testCases.form.testSteps.libraryDialog.loadError' | translate }}</p>
    } @else if (rows().length === 0) {
      <p class="tsl__empty">{{ 'testCases.form.testSteps.libraryDialog.empty' | translate }}</p>
    } @else {
      <!--
        Die Listbox delegiert den Fokus an ihre Optionen (Roving-Tabindex), ist also selbst
        korrekterweise nicht fokussierbar; das (keydown) triggert dennoch die per-Element-Regel.
      -->
      <!-- eslint-disable-next-line @angular-eslint/template/interactive-supports-focus -->
      <div class="tsl__list" role="listbox" aria-multiselectable="true" (keydown)="onListKeydown($event)">
        @for (row of rows(); track row.id; let i = $index) {
          <!--
            Tastatur-Interaktion liegt bewusst auf der Listbox (Roving-Tabindex, onListKeydown),
            nicht pro Zeile; die Zeile ist über [tabindex] fokussierbar. Der Linter prüft aber
            pro Element, daher die gezielte Deaktivierung (Muster wie in test-case-import-modal).
          -->
          <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->
          <div
            #rowRef
            [id]="'tsl-row-' + row.id"
            class="tsl__row"
            [class.tsl__row--selected]="isSelected(row.id)"
            role="option"
            [attr.aria-selected]="isSelected(row.id)"
            [tabindex]="i === activeIndex() ? 0 : -1"
            (click)="onRowClick(i, row.id)"
          >
            <!--
              tabIndex=-1: die Zeile ist der fokussierbare Listbox-Eintrag (Roving-Tabindex),
              die Checkbox darf kein eigener Tab-Stopp sein, sonst hielte Tab an jeder Checkbox.
              Toggle per Tastatur laeuft ueber die Zeile (Enter/Space), Maus-Klick bleibt moeglich.
            -->
            <mat-checkbox
              [checked]="isSelected(row.id)"
              [tabIndex]="-1"
              [attr.aria-label]="('testCases.form.testSteps.libraryDialog.selectAria' | translate) + ' ' + row.label"
              (click)="$event.stopPropagation()"
              (change)="onRowClick(i, row.id)"
            ></mat-checkbox>
            <code class="tsl__id">{{ row.shortId }}</code>
            <span class="tsl__text">
              <span class="tsl__name">{{ row.label }}</span>
              @if (row.secondary) {
                <span class="tsl__desc">{{ row.secondary }}</span>
              }
            </span>
            @if (row.category) {
              <span [class]="row.categoryClass">{{ row.categoryLabel }}</span>
            }
            <span class="tsl__usage" [attr.aria-label]="'testSteps.usageAria' | translate: { count: row.usageCount }">
              {{ 'testSteps.usageValue' | translate: { count: row.usageCount } }}
              {{ 'testSteps.usageUnit' | translate }}
            </span>
          </div>
        }
      </div>
    }
  </div>
</ng-template>
